import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { RequestProposalCard } from "@companion-core/shared/app/Components/Requests/RequestProposalCard";
import { RequestPastRecommendationsProps } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestPastRecommendations/requestPastRecommendations.model";
import { RequestProposalCardVariant } from "@companion-core/shared/app/Components/Requests/RequestProposalCard/requestProposalCard.model";
import { useRequestPastRecommendations } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestPastRecommendations/hooks/useRequestPastRecommendations";
import { trackClickRequestRecommendation } from "@companion-core/shared/app/tracking/requests";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { useRequestCard } from "@companion-core/shared/app/Components/Requests/RequestCard/hooks/useRequestCard";
import { getFamilyIcon } from "@companion-core/shared/app/Utils/myRequests";

import "@companion-core/web/src/Assets/Styles/scss/myRequestsRecommendations.scss";

export const RequestPastRecommendations = ({
  requestData,
  proposalsData,
  contentId,
}: RequestPastRecommendationsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    pastRecommendations,
    getProposalId,
    getRecommendationMedia,
    getRecommendationMediaAlt,
    getRecommendationSubtitle,
    getRecommendationBudget,
    introduction,
    conclusion,
  } = useRequestPastRecommendations({ proposalsData, contentId });
  const { detailTitle } = useRequestCard(requestData);
  const hasDigitalProposal = useMemo(() => requestData.has_digital_proposal, [requestData]);

  useEffect(() => {
    if (!hasDigitalProposal) navigate("/my-requests");
  }, [hasDigitalProposal]);

  return (
    <div
      data-testid="request-detail__past-recommendations"
      className="request-detail__recommendations">
      {conclusion ? (
        <p
          data-testid="request-detail__past-recommendations--description"
          className="body-text-6"
          dangerouslySetInnerHTML={{
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            __html: sanitizeHtml(introduction ?? ""),
          }}
        />
      ) : (
        <div
          className="body-text-6"
          data-testid="request-detail__past-recommendations--description">
          <div>
            {t("request.my-requests.past-recommendations.intro.template", {
              requestTitle: detailTitle.toLowerCase(),
            })}
          </div>
          <div>{t("request.my-requests.past-recommendations.intro.highlight")}</div>
        </div>
      )}
      <div
        data-testid="request-detail__past-recommendations--list"
        className="request-detail__recommendations-list">
        {pastRecommendations.map((recommendation, index) => (
          <RequestProposalCard
            key={recommendation.id}
            testID="request-detail__past-recommendations--card"
            id={recommendation.id}
            tracked
            position={index}
            nbItems={pastRecommendations.length}
            url={window.location.href}
            media={getRecommendationMedia(recommendation)}
            mediaAlt={getRecommendationMediaAlt(recommendation)}
            iconOverlay={
              !getRecommendationMedia(recommendation)
                ? getFamilyIcon(requestData.family ?? "", requestData.sub_family)
                : undefined
            }
            title={recommendation.title}
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            subtitle={sanitizeHtml(getRecommendationSubtitle(recommendation))}
            status={recommendation.status === "validated" ? recommendation.status : ""}
            budget={getRecommendationBudget(recommendation)}
            vendorId={recommendation.vendor_id ?? undefined}
            variant={RequestProposalCardVariant.ModifyReservation}
            onClick={() => {
              trackClickRequestRecommendation({
                family: requestData.family,
                subFamily: requestData.sub_family,
                requestId: requestData.id,
                vendorId: recommendation.vendor_id,
                status: recommendation.status,
              });
              navigate(
                `/my-requests/${requestData.id}/proposals/${getProposalId(recommendation)}/recommendations/${recommendation.id}`,
                {
                  state: { canGoBack: true },
                }
              );
            }}
          />
        ))}
      </div>
      {conclusion ? (
        <p
          data-testid="request-detail__past-recommendations--final-description"
          className="body-text-6"
          dangerouslySetInnerHTML={{
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            __html: sanitizeHtml(conclusion),
          }}
        />
      ) : (
        <div className="body-text-6">
          <div>{t("request.my-requests.past-recommendations.final-description-1")}</div>
          <div>{t("request.my-requests.past-recommendations.final-description-2")}</div>
        </div>
      )}
    </div>
  );
};
